import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Badge } from "../../components/Badge";
import { formatTaipeiDateTime } from "../../lib/datetime";
import type { MailMessage } from "../../api/types";

// 對話串讀信(規格 §2.6):同一串的信(跨資料夾、含寄件備份)**依時間舊到新**排在一起,
// 預設展開最新一封;其他的收成一行(寄件人、時間、主旨),點了展開那一封。
// 展開那一封的內容由頁面給(`children`,就是平常的讀信元件),這裡只管排與收合。

export interface MailThreadViewProps {
  items: MailMessage[];
  expandedId: number | null;
  onExpand: (one: MailMessage) => void;
  /** 展開那一封的內容(讀信元件或「載入中」)。 */
  children: ReactNode;
}

export function MailThreadView({ items, expandedId, onExpand, children }: MailThreadViewProps) {
  return (
    <section aria-label="對話串" className="flex min-w-0 flex-col gap-2">
      <p className="m-0 text-xs text-[var(--text-3)]">這一串共 {items.length} 封,舊的在上面。</p>
      <ol className="m-0 flex flex-col gap-2 p-0">
        {items.map((one) => {
          const who = one.from_name || one.from_addr || "(未知寄件人)";
          if (one.id === expandedId) {
            return (
              <li key={one.id} className="list-none">
                {children}
              </li>
            );
          }
          return (
            <li key={one.id} className="list-none print:hidden">
              <button
                type="button"
                aria-label={`展開:${who},${formatTaipeiDateTime(one.date)},${one.subject || "(無主旨)"}`}
                onClick={() => onExpand(one)}
                className="flex w-full min-w-0 items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-left text-sm hover:bg-[var(--surface-2)]"
              >
                <ChevronRight aria-hidden="true" size={14} className="shrink-0 text-[var(--text-3)]" />
                <span className="min-w-0 flex-1 truncate text-[var(--text)]">{who}</span>
                {one.deleted_on_server ? <Badge tone="neutral">雲端已刪除</Badge> : null}
                {!one.seen && !one.deleted_on_server ? <Badge tone="accent">未讀</Badge> : null}
                <span className="shrink-0 text-xs tabular text-[var(--text-3)]">{formatTaipeiDateTime(one.date)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
